import { ApiError } from '../../api/apiError';
import { AgencyTerminology, agencyTerminology } from '../../components/agency/agencyTerminology';

// Server error codes are shared by both signup forms, so the message for one is written in the
// wording of whichever form the sponsor is actually looking at.
const agencyErrorMessages = (t: AgencyTerminology): Record<string, string> => ({
  AGENCY_ALREADY_EXISTS:
    `An ${t.org} is already registered for this domain. Contact CLIPr support if this is unexpected.`,
  SPONSOR_EMAIL_IN_USE: 'An account already exists for this admin email. Sign in or use a different email.',
  WEAK_SPONSOR_PASSWORD: 'That password was rejected. Choose a stronger password and try again.',
  AGENCY_SIGNUP_UNAVAILABLE: 'We could not complete signup right now. Please try again in a few minutes.',
  PUBLIC_EMAIL_DOMAIN: `Use your ${t.org} email address. Public domains like gmail.com are not allowed.`,
  SPONSOR_DOMAIN_MISMATCH: `Admin email must use the same domain as the ${t.org} domain.`,
  OFFICER_DOMAIN_MISMATCH: `${t.Officer} and ${t.supervisor} emails must use the ${t.org} domain.`,
  INVALID_ROSTER_SIZE: 'Roster size does not match the required minimum or exceeds the maximum.',
  INVALID_PASSWORD: 'Password must be at least 8 characters and include a number.',
  TERMS_REQUIRED: 'You must accept the CLIPr Terms and Privacy Policy to continue.',
});

const FALLBACK_MESSAGE = 'Something went wrong. Please review your details and try again. Contact CLIPr support if the problem persists.';
const NETWORK_OFFLINE_MESSAGE = 'You appear to be offline. Reconnect and try again.';

export function resolveAgencySignupError(err: unknown, terms: AgencyTerminology = agencyTerminology()): string {
  const messages = agencyErrorMessages(terms);
  if (err instanceof ApiError) {
    if (err.type === 'NetworkOffline')
      return NETWORK_OFFLINE_MESSAGE;

    const graphQlErrors = err.data.graphQLError?.errors ?? [];
    for (const graphQlError of graphQlErrors) {
      const mapped = mapKnownErrorType(graphQlError.errorType ?? null, messages);
      if (mapped)
        return mapped;

      const messageFromCode = extractCodeFromMessage(graphQlError.message, messages);
      if (messageFromCode)
        return messageFromCode;

      if (looksLikeReadableMessage(graphQlError.message))
        return graphQlError.message;
    }

    if (looksLikeReadableMessage(err.originalMessage))
      return err.originalMessage!;
  }

  if (err && typeof err === 'object' && 'message' in err && looksLikeReadableMessage((err as { message: unknown }).message))
    return (err as { message: string }).message;

  return FALLBACK_MESSAGE;
}

function mapKnownErrorType(errorType: string | null, messages: Record<string, string>): string | null {
  if (!errorType)
    return null;

  return messages[errorType] ?? null;
}

function extractCodeFromMessage(message: string | undefined, messages: Record<string, string>): string | null {
  if (!message)
    return null;

  for (const code of Object.keys(messages)) {
    if (message.includes(code))
      return messages[code];
  }
  return null;
}

function looksLikeReadableMessage(message: unknown): message is string {
  if (typeof message !== 'string' || !message.trim())
    return false;

  if (/^[A-Z][A-Za-z]*Error$/.test(message.trim()))
    return false;

  return true;
}
